import { Platform, View } from "react-native"
import { cn } from "@/components/util/cn"
import GoogleLoginButton, {
  type GoogleCode,
} from "@/components/buttons/login/GoogleLoginButton"
import config from "@config"
import AppleLoginButton from "@/components/buttons/login/AppleLoginButton"
import * as registerEvents from "@/analytics/events/register"
import { GOOGLE_SCOPES } from "@/constants/Scopes"
import { router } from "expo-router"
import { showErrorToast } from "@/components/toasts/AppToast"
import type { AppleAuthenticationCredential } from "expo-apple-authentication/src/AppleAuthentication.types"
import type { ClientError } from "graphql-request"
import { useRegistrationInputs } from "@/stores/register"
import { usePreQualState } from "@/stores/preQual"
import {
  useAppleLoginAuthMutation,
  useGoogleLoginAuthMutation,
} from "@/queries/user"
import { Vertical } from "@/components/standard/BFLayout"
import { fbqTrackCustom, fbqUserAttributes } from "@/utils/PixelUtils"

interface SocialLoginProps {
  loginType: "login" | "register"
}

export const SocialLogin = ({ loginType }: SocialLoginProps) => {
  const {
    setFirstName,
    setLastName,
    setConfirmationMode,
    setGoogleCode,
    setAppleCode,
  } = useRegistrationInputs()

  const googleLoginAuthMutation = useGoogleLoginAuthMutation()
  const appleLoginAuthMutation = useAppleLoginAuthMutation()
  const preQualState = usePreQualState()
  const fbqAttributes = fbqUserAttributes(preQualState, null)

  const onGoogleLogin = (code: GoogleCode) => {
    if (code?.idToken) {
      registerEvents.googleAuthRegistrationSubmitted({
        email: code.user.email,
        userExists: true,
      })
      fbqAttributes.email = code?.user.email
      fbqTrackCustom("UserSignup", fbqAttributes)
      googleLoginAuthMutation.mutate(
        {
          serviceType: "auth",
          idToken: code.idToken,
          scopes: GOOGLE_SCOPES,
          redirectUri: code?.redirectUri,
        },
        {
          onSuccess: (data) => {
            if (data?.googleLogin?.token) {
              console.log("Login to google successful")
            } else {
              showErrorToast({
                title: "Sign In Error",
                message: "Could not complete sign in.",
              })
            }
          },
          onError: (error) => {
            // @ts-expect-error google error
            const errorResponse = error?.response
            if (errorResponse.errors) {
              const errorMessage = String(errorResponse.errors[0].message)
              const userNotFoundErrors = [
                "GOOGLE_NO_USER_FOUND",
                "User matching query does not exist.",
              ]

              if (userNotFoundErrors.includes(errorMessage)) {
                registerEvents.googleAuthRegistrationSubmitted({
                  email: code.user.email,
                  userExists: false,
                })
                if (code?.user.givenName) {
                  setFirstName(code.user.givenName)
                }

                if (code?.user.familyName) {
                  setLastName(code.user.familyName)
                }
                setConfirmationMode("google")
                setGoogleCode(code)
                router.push("/confirm")
              }
            }
          },
        }
      )
    }
  }

  const onAppleLogin = (code: AppleAuthenticationCredential) => {
    if (code?.identityToken) {
      registerEvents.appleAuthRegistrationSubmitted({
        email: code.email || "",
        userExists: true,
      })
      fbqAttributes.email = code.email || ""
      /*
       * This probably will just be a no-op but leaving it here in case we refactor the fbqTrackCustom function
       * to use a different API on mobile in the future. Currently it depends on Pixel, which is a web-only library.
       * */
      fbqTrackCustom("UserSignup", fbqAttributes)
      appleLoginAuthMutation.mutate(
        {
          serviceType: "auth",
          idToken: code.identityToken,
        },
        {
          onSuccess: (data) => {
            if (data?.appleLogin?.token) {
              console.log("Login to apple successful")
            } else {
              showErrorToast({
                title: "Sign In Error",
                message: "Could not complete sign in.",
              })
            }
          },
          onError: (error) => {
            console.log(error)
            const errorResponse = (error as ClientError)?.response
            if (errorResponse.errors) {
              const errorMessage = String(errorResponse.errors[0].message)
              const userNotFoundErrors = [
                "APPLE_NO_USER_FOUND",
                "User matching query does not exist.",
              ]

              if (userNotFoundErrors.includes(errorMessage)) {
                registerEvents.appleAuthRegistrationSubmitted({
                  email: code.email || "",
                  userExists: false,
                })
                if (code?.fullName?.givenName) {
                  setFirstName(code.fullName.givenName)
                }

                if (code?.fullName?.familyName) {
                  setLastName(code.fullName.familyName)
                }
                setConfirmationMode("apple")
                setAppleCode(code)
                router.push("/confirm")
              }
            }
          },
        }
      )
    }
  }

  return (
    <Vertical>
      <View className={cn("w-full")}>
        <GoogleLoginButton
          onLogin={onGoogleLogin}
          authCode={true}
          redirectUri={config.GOOGLE_REDIRECT_URI}
        >
          {loginType === "register"
            ? "Sign Up with Google"
            : "Sign In with Google"}
        </GoogleLoginButton>
      </View>
      {Platform.OS === "ios" && (
        <AppleLoginButton onLogin={onAppleLogin} isRegister />
      )}
    </Vertical>
  )
}
